import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, Flag, Trophy, X, ArrowRight, ArrowLeft } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useSyncedState from './useSyncedState';
import ReferenceScale from './ReferenceScale';

// Ходилка — a snake-shaped board of task cells. Players take turns rolling the
// built-in die; the token hops cell by cell, and the landing cell's task pops
// up. Cells can carry a jump (+N forward / −N back). First to Finish wins.
//
// Positions index into [Start, ...cells, Finish]. Game state is one synced
// blob: { positions: { playerId: idx }, turn, lastRoll, openCell, winnerId, rev }.

const HOP_MS = 230;
const ROLL_MS = 650;

export const BOARD_DEFAULT_PLAYER_COLORS = ['#2A4D25', '#e11d48', '#2563eb', '#f59e0b'];

const createInitialState = () => ({
  positions: {},
  turn: 0,
  lastRoll: null,
  openCell: null,
  winnerId: null,
});

const PIPS = {
  1: [[50, 50]],
  2: [[28, 28], [72, 72]],
  3: [[28, 28], [50, 50], [72, 72]],
  4: [[28, 28], [72, 28], [28, 72], [72, 72]],
  5: [[28, 28], [72, 28], [50, 50], [28, 72], [72, 72]],
  6: [[28, 25], [72, 25], [28, 50], [72, 50], [28, 75], [72, 75]],
};

const DieFace = ({ value, size, color }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: 'block' }}>
    <rect x="4" y="4" width="92" height="92" rx="20" fill="#ffffff" stroke={color} strokeWidth="6" />
    {(PIPS[value] || []).map(([cx, cy], i) => (
      <circle key={i} cx={cx} cy={cy} r="9" fill={color} />
    ))}
  </svg>
);

// Token position that hops one cell at a time toward `target`.
const useHoppingPositions = (targets) => {
  const [shown, setShown] = useState(targets);
  const targetsKey = JSON.stringify(targets);
  useEffect(() => {
    const id = setInterval(() => {
      setShown((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.entries(targets).forEach(([pid, target]) => {
          const cur = prev[pid] ?? 0;
          if (cur !== target) {
            next[pid] = cur + (target > cur ? 1 : -1);
            changed = true;
          }
        });
        Object.keys(prev).forEach((pid) => { if (!(pid in targets)) { delete next[pid]; changed = true; } });
        return changed ? next : prev;
      });
    }, HOP_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey]);
  return shown;
};

const BoardGameInner = ({
  element,
  isEditor = false,
  canInteract = true,
  syncedState = null,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const tg = (k, v) => t(`games.boardGame.${k}`, v);
  const {
    title = '',
    cells = [],
    players = [],
    columns = 6,
    startLabel = 'Start',
    finishLabel = 'Finish',
    cellColor = '#ffffff',
    boardColor = '#F3EFEA',
    accentColor = '#2A4D25',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 14,
  } = element.content || {};

  const [state, update] = useSyncedState(syncedState, onStateChange, createInitialState);
  const [rollingFace, setRollingFace] = useState(null);
  const rollTimerRef = useRef(null);
  useEffect(() => () => clearInterval(rollTimerRef.current), []);

  const squares = useMemo(() => [
    { id: '__start', text: startLabel, kind: 'start' },
    ...cells.map((c) => ({ ...c, kind: 'cell' })),
    { id: '__finish', text: finishLabel, kind: 'finish' },
  ], [cells, startLabel, finishLabel]);
  const lastIdx = squares.length - 1;
  const cols = Math.max(2, Math.min(12, Number(columns) || 6));
  const rows = Math.ceil(squares.length / cols);
  const coordOf = (i) => {
    const r = Math.floor(i / cols);
    const inRow = i % cols;
    const c = r % 2 === 0 ? inRow : cols - 1 - inRow;
    return { r, c };
  };

  const activePlayers = players.length > 0 ? players : [{ id: '__p1', name: 'Player 1', color: BOARD_DEFAULT_PLAYER_COLORS[0] }];
  const targets = Object.fromEntries(activePlayers.map((p) => [p.id, Math.min(lastIdx, Math.max(0, state.positions?.[p.id] ?? 0))]));
  const shown = useHoppingPositions(targets);
  const turnIdx = (state.turn || 0) % activePlayers.length;
  const current = activePlayers[turnIdx];
  const winner = activePlayers.find((p) => p.id === state.winnerId) || null;
  const animating = Object.keys(targets).some((pid) => (shown[pid] ?? 0) !== targets[pid]);
  const interactive = !isEditor && canInteract;
  const canRoll = interactive && !winner && rollingFace === null && !animating && state.openCell === null;

  const roll = () => {
    if (!canRoll) return;
    const value = 1 + Math.floor(Math.random() * 6);
    const startedAt = Date.now();
    rollTimerRef.current = setInterval(() => {
      if (Date.now() - startedAt >= ROLL_MS) {
        clearInterval(rollTimerRef.current);
        setRollingFace(null);
        update((prev) => {
          const pos = prev.positions?.[current.id] ?? 0;
          const landed = Math.min(lastIdx, pos + value);
          const jump = squares[landed]?.kind === 'cell' ? (Number(squares[landed].jump) || 0) : 0;
          const finalPos = Math.min(lastIdx, Math.max(0, landed + jump));
          const won = finalPos === lastIdx;
          return {
            ...prev,
            positions: { ...prev.positions, [current.id]: finalPos },
            lastRoll: value,
            openCell: squares[landed]?.kind === 'cell' ? landed : null,
            turn: won ? prev.turn : (prev.turn || 0) + 1,
            winnerId: won ? current.id : null,
          };
        });
        return;
      }
      setRollingFace(1 + Math.floor(Math.random() * 6));
    }, 80);
    setRollingFace(value);
  };

  const closeCard = () => update((prev) => ({ ...prev, openCell: null }));
  const reset = () => update(() => createInitialState());

  const fs = fontSize;
  const openSquare = state.openCell !== null && state.openCell !== undefined ? squares[state.openCell] : null;
  const dieValue = rollingFace ?? state.lastRoll ?? 1;

  return (
    <div
      className="w-full h-full flex flex-col overflow-hidden rounded-2xl"
      style={{ fontFamily, color: textColor, backgroundColor: boardColor, padding: 10, gap: 8 }}
    >
      {title && (
        <div className="text-center font-bold leading-tight shrink-0" style={{ fontSize: fs * 1.4 }}>{title}</div>
      )}

      <div className="relative flex-1 min-h-0">
        <div
          className="absolute inset-0"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            gap: 6,
          }}
        >
          {squares.map((sq, i) => {
            const { r, c } = coordOf(i);
            const special = sq.kind !== 'cell';
            const jump = Number(sq.jump) || 0;
            return (
              <div
                key={sq.id}
                onClick={() => { if (interactive && sq.kind === 'cell' && state.openCell === null) update((prev) => ({ ...prev, openCell: i })); }}
                className="relative rounded-xl flex flex-col items-center justify-center text-center overflow-hidden"
                style={{
                  gridRow: r + 1,
                  gridColumn: c + 1,
                  backgroundColor: special ? accentColor : cellColor,
                  color: special ? '#ffffff' : textColor,
                  border: `2px solid ${special ? accentColor : 'rgba(0,0,0,0.08)'}`,
                  padding: 4,
                  cursor: interactive && sq.kind === 'cell' ? 'pointer' : 'default',
                }}
              >
                {!special && (
                  <span className="absolute top-1 left-1.5 font-semibold opacity-40 leading-none" style={{ fontSize: fs * 0.7 }}>{i}</span>
                )}
                {sq.kind === 'finish' && <Flag size={fs * 1.1} className="mb-0.5" />}
                {sq.imageUrl ? (
                  <img src={resolveMediaUrl(sq.imageUrl) || ''} alt="" draggable={false} className="max-w-full min-h-0 flex-1 object-contain" />
                ) : null}
                {sq.text && (
                  <div
                    className={`leading-tight break-words w-full ${special ? 'font-bold' : ''}`}
                    style={{
                      fontSize: special ? fs * 1.05 : fs * 0.85,
                      display: '-webkit-box',
                      WebkitLineClamp: sq.imageUrl ? 1 : 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {sq.text}
                  </div>
                )}
                {jump !== 0 && (
                  <span
                    className="absolute bottom-1 right-1.5 inline-flex items-center rounded-full font-bold leading-none"
                    style={{ fontSize: fs * 0.7, padding: '2px 5px', backgroundColor: jump > 0 ? '#dcfce7' : '#fee2e2', color: jump > 0 ? '#166534' : '#991b1b' }}
                  >
                    {jump > 0 ? <ArrowRight size={fs * 0.7} /> : <ArrowLeft size={fs * 0.7} />}
                    {Math.abs(jump)}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Tokens — absolutely positioned over the grid so hops animate. */}
        <div className="absolute inset-0 pointer-events-none">
          {activePlayers.map((p, pi) => {
            const pos = shown[p.id] ?? 0;
            const { r, c } = coordOf(pos);
            const sameCell = activePlayers.filter((q) => (shown[q.id] ?? 0) === pos);
            const k = sameCell.findIndex((q) => q.id === p.id);
            const offset = (k - (sameCell.length - 1) / 2) * 14;
            return (
              <div
                key={p.id}
                className="absolute rounded-full border-2 border-white shadow-md"
                style={{
                  width: 22,
                  height: 22,
                  left: `calc(${((c + 0.5) / cols) * 100}% + ${offset}px)`,
                  top: `calc(${((r + 0.5) / rows) * 100}% + 6px)`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: p.color || BOARD_DEFAULT_PLAYER_COLORS[pi % 4],
                  transition: `left ${HOP_MS}ms ease, top ${HOP_MS}ms ease`,
                  zIndex: 2 + pi,
                }}
                title={p.name}
              />
            );
          })}
        </div>

        {/* Task card for the landing cell */}
        {openSquare && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/25 rounded-xl">
            <div className="relative bg-white rounded-2xl shadow-xl flex flex-col items-center text-center" style={{ maxWidth: '75%', maxHeight: '90%', padding: 18, gap: 10 }}>
              {interactive && (
                <button type="button" onClick={closeCard} className="absolute top-2 right-2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                  <X size={16} />
                </button>
              )}
              <span className="font-semibold opacity-50" style={{ fontSize: fs * 0.8 }}>#{state.openCell}</span>
              {openSquare.imageUrl && (
                <img src={resolveMediaUrl(openSquare.imageUrl) || ''} alt="" draggable={false} className="max-h-40 max-w-full object-contain" />
              )}
              {openSquare.text && (
                <div className="font-semibold leading-snug break-words" style={{ fontSize: fs * 1.4, color: textColor }}>{openSquare.text}</div>
              )}
              {(Number(openSquare.jump) || 0) !== 0 && (
                <div className="font-medium" style={{ fontSize: fs, color: openSquare.jump > 0 ? '#166534' : '#991b1b' }}>
                  {openSquare.jump > 0
                    ? tg('jumpForward', { count: openSquare.jump, defaultValue: `Вперед на ${openSquare.jump}` })
                    : tg('jumpBack', { count: Math.abs(openSquare.jump), defaultValue: `Назад на ${Math.abs(openSquare.jump)}` })}
                </div>
              )}
              {interactive && (
                <button
                  type="button"
                  onClick={closeCard}
                  className="px-5 py-1.5 rounded-full text-white font-medium bg-[#2A4D25] hover:bg-[#1e3a1a] transition"
                  style={{ fontSize: fs }}
                >
                  {tg('done', { defaultValue: 'Готово' })}
                </button>
              )}
            </div>
          </div>
        )}

        {winner && !animating && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/25 rounded-xl">
            <div className="bg-white rounded-2xl shadow-xl flex flex-col items-center text-center" style={{ padding: 20, gap: 10 }}>
              <Trophy size={40} style={{ color: winner.color || accentColor }} />
              <div className="font-bold" style={{ fontSize: fs * 1.5 }}>{tg('winner', { name: winner.name, defaultValue: `${winner.name} перемагає!` })}</div>
              {interactive && (
                <button
                  type="button"
                  onClick={reset}
                  className="px-5 py-1.5 rounded-full text-white font-medium bg-[#2A4D25] hover:bg-[#1e3a1a] transition"
                  style={{ fontSize: fs }}
                >
                  {tg('playAgain', { defaultValue: 'Грати ще' })}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Turn bar */}
      <div className="shrink-0 flex items-center justify-between gap-3 bg-white/70 rounded-xl" style={{ padding: '6px 10px' }}>
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {activePlayers.map((p, pi) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 rounded-full font-medium"
              style={{
                fontSize: fs * 0.9,
                padding: '3px 10px',
                backgroundColor: pi === turnIdx && !winner ? (p.color || accentColor) : 'transparent',
                color: pi === turnIdx && !winner ? '#ffffff' : textColor,
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full border border-white" style={{ backgroundColor: p.color || BOARD_DEFAULT_PLAYER_COLORS[pi % 4] }} />
              {p.name}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {interactive && (
            <button
              type="button"
              onClick={reset}
              title={tg('reset', { defaultValue: 'Почати спочатку' })}
              className="p-1.5 rounded-full text-gray-500 hover:text-gray-800 hover:bg-white transition"
            >
              <RotateCcw size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={roll}
            disabled={!canRoll}
            className="flex items-center gap-2 rounded-full text-white font-semibold transition disabled:opacity-60 bg-[#2A4D25] hover:bg-[#1e3a1a] disabled:hover:bg-[#2A4D25]"
            style={{ fontSize: fs, padding: '4px 14px 4px 4px', cursor: canRoll ? 'pointer' : 'default' }}
          >
            <span className={rollingFace !== null ? 'animate-spin' : ''} style={{ animationDuration: '0.5s' }}>
              <DieFace value={dieValue} size={28} color={accentColor} />
            </span>
            {tg('roll', { defaultValue: 'Кинути' })}
          </button>
        </div>
      </div>
    </div>
  );
};

const BoardGameElement = ({ slideScale = 1, ...props }) => (
  <ReferenceScale scale={slideScale}>
    <BoardGameInner {...props} />
  </ReferenceScale>
);

export default BoardGameElement;
